import { Box, Flex } from 'rebass'
import { StaticQuery, graphql } from 'gatsby'

import Color from 'color'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import IconButton from '@material-ui/core/IconButton'
import Img from 'gatsby-image'
import Link from './link'
import MediaQuery from 'react-responsive'
import React from 'react'
import { Trans } from '@lingui/macro'
import { css } from 'glamor'
import locales from '../utils/locales'
import theme from '../utils/theme'
import withScrollPosition from '../utils/withScrollPosition'
import zIndex from '@material-ui/core/styles/zIndex'

const HeaderLink = ({ children, iconOnly, to, ...otherProps }) => {
  const isInternal = to.startsWith('/')
  const LinkElement = isInternal ? Link : 'a'

  return (
    <LinkElement
      css={{
        '& > span:before': {
          content: '""',
          position: 'absolute',
          top: 'calc(100% + .5vw)',
          left: '-1vw',
          width: 'calc(100% + 2vw)',
          height: 1,
          background: `linear-gradient(to right, transparent, ${
            theme.colors.tertiary
          }, transparent)`,
          transition: 'transform 400ms, opacity 400ms',
          transform: 'scaleX(0)',
          // opacity: 0,
        },
        '&:hover': {
          color: theme.colors.tertiary,
          '& > span:before': {
            transform: 'scaleX(1)',
            // opacity: 1,
          },
        },
        flex: `${iconOnly ? 1 : 5} 1 auto`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: theme.colors.primary,
        textDecoration: 'none',
        transition: 'color 400ms',
      }}
      {...(isInternal ? { to } : { href: to })}
      {...otherProps}
    >
      <span style={{ position: 'relative' }}>{children}</span>
    </LinkElement>
  )
}

const logoHoverStyle = css({
  '&:before': {
    transform: 'scaleX(1)',
  },
  boxShadow: `0 0 2vw ${Color('black').fade(0.88)}`,
  transform: 'scale(1.1)',
})

const logoBaseStyle = css({
  '&:before': {
    content: '""',
    position: 'absolute',
    top: '57.5%',
    left: '11.5%',
    width: '77%',
    height: 1,
    background: 'black',
    transform: 'scaleX(0)',
    transition: 'transform 400ms',
  },
  '&:hover': logoHoverStyle,
  position: 'relative',
  display: 'block',
  padding: '1vw',
  width: '9vw',
  height: '9vw',
  borderRadius: '50%',
  background: 'white',
  boxShadow: `0 0 1vw ${Color('black').fade(0.88)}`,
  transform: 'scale(1)',
  transition: 'box-shadow 400ms, transform 400ms',
})

const MobileHeaderLink = ({ children, delay, open, to, ...otherProps }) => {
  const isInternal = to.startsWith('/')
  const LinkElement = isInternal ? Link : 'a'

  return (
    <LinkElement
      css={{
        '& + *': {
          borderTop: `1px solid ${Color(theme.colors.primary).fade(0.88)}`,
        },
        '&:active': {
          color: 'black',
        },
        display: 'flex',
        alignItems: 'center',
        height: 48,
        color: theme.colors.primary,
        textDecoration: 'none',
        transitionProperty: 'opacity',
        transitionDuration: '400ms',
        ...(open
          ? {
              transitionDelay: `calc(312ms + ${88 * delay}ms)`,
              opacity: 1,
            }
          : {
              opacity: 0,
            }),
      }}
      {...(isInternal ? { to } : { href: to })}
      {...otherProps}
    >
      {children}
    </LinkElement>
  )
}

const LocaleToggle = ({ locale, location, mobile, ...otherProps }) => {
  return (
    <HeaderLink
      to={location.pathname
        .split('/')
        .map((part, index) => {
          if (index === 1) {
            if (Object.keys(locales).indexOf(part) > -1) {
              return locale === 'cs' ? 'en' : '..'
            } else {
              return locale === 'cs' ? ['en', part] : part
            }
          }

          return part
        })
        .reduce((arr, val) => arr.concat(val), [])
        .join('/')}
      replace
      style={{
        ...(mobile
          ? {}
          : {
              '&:active': {
                color: 'black',
              },
            }),
      }}
      {...otherProps}
    >
      {!mobile && (
        <>
          <FontAwesomeIcon icon="language" size="lg" />{' '}
        </>
      )}
      {
        locales[locale === 'cs' ? 'en' : 'cs'][
          mobile ? 'localeShort' : 'locale'
        ]
      }
    </HeaderLink>
  )
}

class Header extends React.Component {
  state = {
    isClient: false,
    open: false,
  }

  handleToggle() {
    this.setState({ open: !this.state.open }, () => {
      if (this.state.open) {
        document.querySelector('html').style.overflow = 'hidden'
      } else {
        document.querySelector('html').style.overflow = 'auto'
      }
    })
  }

  componentDidMount() {
    this.setState({ isClient: true })
  }

  render() {
    const { locale, location, scrollY } = this.props

    if (!this.state.isClient) {
      return false
    }

    return (
      <StaticQuery
        query={graphql`
          {
            logoSquare: file(relativePath: { eq: "images/logo-square.png" }) {
              childImageSharp {
                fluid {
                  ...GatsbyImageSharpFluid
                }
              }
            }

            logoTooth: file(relativePath: { eq: "images/logo-tooth.png" }) {
              childImageSharp {
                fixed(width: 25, height: 40) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
        `}
        render={({ logoSquare, logoTooth }) => {
          return (
            <>
              <MediaQuery query="(max-width: 991.98px)">
                <Box
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    left: 0,
                    zIndex: zIndex.appBar,
                    overflow: 'hidden',
                    boxShadow: `0 0 1vw ${Color('black').fade(0.88)}`,
                    ...(this.state.open
                      ? {
                          height: '100%',
                          background: 'white',
                          transition: `
                          background .36s cubic-bezier(0.32, 0.08, 0.24, 1),
                          height .56s cubic-bezier(0.52, 0.16, 0.24, 1)
                        `,
                        }
                      : {
                          height: 48,
                          background: Color('white').fade(0.01),
                          transition: `
                          background .44s .2s cubic-bezier(0.52, 0.16, 0.24, 1),
                          height .56s cubic-bezier(0.52, 0.16, 0.24, 1)
                        `,
                        }),
                  }}
                >
                  <Box
                    style={{
                      position: 'relative',
                      height: 48,
                      borderBottom: `1px solid ${Color(
                        theme.colors.primary
                      ).fade(0.88)}`,
                    }}
                  >
                    <Box style={{ position: 'absolute', top: 0, left: 0 }}>
                      <IconButton
                        onClick={() => {
                          this.handleToggle()
                        }}
                      >
                        <FontAwesomeIcon
                          fixedWidth
                          icon={this.state.open ? 'times' : 'bars'}
                          style={{ width: 24, height: 24 }}
                        />
                      </IconButton>
                    </Box>
                    <Box
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <Link
                        to="/"
                        onClick={() => {
                          if (this.state.open) {
                            this.handleToggle()
                          }
                        }}
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="center"
                          p={1}
                          style={{ width: 48, height: 48 }}
                        >
                          <Img
                            fixed={logoTooth.childImageSharp.fixed}
                            imgStyle={{ margin: 0 }}
                          />
                        </Flex>
                      </Link>
                    </Box>
                    <Flex
                      px={3}
                      justifyContent="center"
                      alignItems="center"
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: 48,
                      }}
                    >
                      <LocaleToggle
                        locale={locale}
                        location={location}
                        mobile
                        onClick={() => {
                          if (this.state.open) {
                            this.handleToggle()
                          }
                        }}
                      />
                    </Flex>
                  </Box>
                  <Box
                    px={5}
                    pt={2}
                    pb={3}
                    style={{
                      overflow: 'auto',
                    }}
                  >
                    {/*<MobileHeaderLink
                    to="/#services"
                    open={this.state.open}
                    delay={1}
                    onClick={() => {
                      this.handleToggle()
                    }}
                  >
                    <Trans>Služby</Trans>
                  </MobileHeaderLink>
                  <MobileHeaderLink
                    to="/#gallery"
                    open={this.state.open}
                    delay={2}
                    onClick={() => {
                      this.handleToggle()
                    }}
                  >
                    <Trans>Galerie</Trans>
                  </MobileHeaderLink>
                  <MobileHeaderLink
                    to="/#testimonials"
                    open={this.state.open}
                    delay={3}
                    onClick={() => {
                      this.handleToggle()
                    }}
                  >
                    <Trans>Ohlasy</Trans>
                  </MobileHeaderLink>*/}
                    <MobileHeaderLink
                      to="/#team"
                      open={this.state.open}
                      delay={4}
                      onClick={() => {
                        this.handleToggle()
                      }}
                    >
                      <Trans>Tým</Trans>
                    </MobileHeaderLink>
                    <MobileHeaderLink
                      to="/#pricing"
                      open={this.state.open}
                      delay={5}
                      onClick={() => {
                        this.handleToggle()
                      }}
                    >
                      <Trans>Ceník</Trans>
                    </MobileHeaderLink>
                    <MobileHeaderLink
                      to="/#contact"
                      open={this.state.open}
                      delay={6}
                      onClick={() => {
                        this.handleToggle()
                      }}
                    >
                      <Trans>Kontakt</Trans>
                    </MobileHeaderLink>
                    <MobileHeaderLink
                      target="_blank"
                      to="https://www.facebook.com/KlikaDent/"
                      open={this.state.open}
                      delay={7}
                    >
                      <FontAwesomeIcon
                        fixedWidth
                        icon={['fab', 'facebook']}
                        size="lg"
                      />
                    </MobileHeaderLink>
                    <MobileHeaderLink
                      target="_blank"
                      to="https://www.instagram.com/klikadent/"
                      open={this.state.open}
                      delay={8}
                    >
                      <FontAwesomeIcon
                        fixedWidth
                        icon={['fab', 'instagram']}
                        size="lg"
                      />
                    </MobileHeaderLink>
                    <MobileHeaderLink
                      target="_blank"
                      to="https://www.linkedin.com/company/klikadent/"
                      open={this.state.open}
                      delay={9}
                    >
                      <FontAwesomeIcon
                        fixedWidth
                        icon={['fab', 'linkedin']}
                        size="lg"
                      />
                    </MobileHeaderLink>
                  </Box>
                </Box>
              </MediaQuery>
              <MediaQuery query="(min-width: 992px)">
                <>
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      padding: '0 15vw',
                      height: '25vw',
                      background:
                        'linear-gradient(to bottom, black, transparent)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        height: '8vw',
                        fontSize: '1.1rem',
                      }}
                    >
                      {/*<span style={{ flex: 0.5 }} />*/}
                      {/*<HeaderLink to="/#services">
                      <Trans>Služby</Trans>
                    </HeaderLink>
                    <HeaderLink to="/#gallery">
                      <Trans>Galerie</Trans>
                    </HeaderLink>
                    <HeaderLink to="/#testimonials">
                      <Trans>Ohlasy</Trans>
                    </HeaderLink>*/}
                      <HeaderLink to="/#team">
                        <Trans>Tým</Trans>
                      </HeaderLink>
                      <HeaderLink to="/#pricing">
                        <Trans>Ceník</Trans>
                      </HeaderLink>
                      <HeaderLink to="/#contact">
                        <Trans>Kontakt</Trans>
                      </HeaderLink>
                      <LocaleToggle locale={locale} location={location} />
                      <HeaderLink
                        iconOnly
                        target="_blank"
                        to="https://www.facebook.com/KlikaDent/"
                      >
                        <FontAwesomeIcon icon={['fab', 'facebook']} size="lg" />
                      </HeaderLink>
                      <HeaderLink
                        iconOnly
                        target="_blank"
                        to="https://www.instagram.com/klikadent/"
                      >
                        <FontAwesomeIcon
                          icon={['fab', 'instagram']}
                          size="lg"
                        />
                      </HeaderLink>
                      <HeaderLink
                        iconOnly
                        target="_blank"
                        to="https://www.linkedin.com/company/klikadent/"
                      >
                        <FontAwesomeIcon icon={['fab', 'linkedin']} size="lg" />
                      </HeaderLink>
                      {/*<span style={{ flex: 0.5 }} />*/}
                    </div>
                  </div>
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      right: 0,
                      left: 0,
                      zIndex: 100,
                      transform: 'translate3d(0, 0, 0)',
                    }}
                  >
                    <div
                      style={{
                        position: 'fixed',
                        top: '3vw',
                        left: '3vw',
                        zIndex: 1,
                      }}
                    >
                      <Link
                        to="/"
                        {...(scrollY <= 0
                          ? css(logoBaseStyle, logoHoverStyle)
                          : logoBaseStyle)}
                      >
                        <Img
                          fluid={logoSquare.childImageSharp.fluid}
                          imgStyle={{ margin: 0 }}
                        />
                      </Link>
                    </div>
                  </div>
                </>
              </MediaQuery>
            </>
          )
        }}
      />
    )
  }
}

export default withScrollPosition(Header)
